// The models an administrator may give the assistant (ADR-284): Claude through the API of Anthropic, or a model of
// Workers AI. Only models that call tools; the prices are per million tokens in USD, from the price lists of
// 2026-10-24, and count towards the monthly cap (`assistant.monthly_cap_usd`). A model leaves the list only after the
// parameter that names it has moved on: a value the contract no longer knows counts as never set.

export type AssistantProvider = "anthropic" | "workers-ai";

export interface AssistantModel {
  provider: AssistantProvider;
  /** The name an administrator chooses it by. */
  label: string;
  /** USD per million tokens: read fresh, read from the provider's cache, written to it, and generated. */
  price: { input: number; cachedInput: number; cacheWrite: number; output: number };
}

export const assistantModels = {
  "claude-opus-5-5": {
    provider: "anthropic",
    label: "Claude Opus 5.5",
    price: { input: 4, cachedInput: 0.2, cacheWrite: 5, output: 20 },
  },
  "claude-sonnet-5-5": {
    provider: "anthropic",
    label: "Claude Sonnet 5.5",
    price: { input: 2, cachedInput: 0.2, cacheWrite: 2.5, output: 10 },
  },
  "claude-haiku-5-5": {
    provider: "anthropic",
    label: "Claude Haiku 5.5",
    price: { input: 0.1, cachedInput: 0.01, cacheWrite: 0.125, output: 0.5 },
  },
  "@cf/moonshotai/kimi-k2.6": {
    provider: "workers-ai",
    label: "Kimi K2.6 (Workers AI)",
    price: { input: 0.95, cachedInput: 0.16, cacheWrite: 0.95, output: 4 },
  },
  "@cf/openai/gpt-oss-120b": {
    provider: "workers-ai",
    label: "gpt-oss-120b (Workers AI)",
    price: { input: 0.35, cachedInput: 0.35, cacheWrite: 0.35, output: 0.75 },
  },
  "@cf/meta/llama-4-scout-17b-16e-instruct": {
    provider: "workers-ai",
    label: "Llama 4 Scout (Workers AI)",
    price: { input: 0.27, cachedInput: 0.27, cacheWrite: 0.27, output: 0.85 },
  },
} as const satisfies Record<string, AssistantModel>;

export type AssistantModelId = keyof typeof assistantModels;

export const assistantModelIds = Object.keys(assistantModels) as [AssistantModelId, ...AssistantModelId[]];

/** What a turn used of a model, as the provider counted it. */
export interface AssistantUsage {
  /** Read fresh: every input token neither read from the cache nor written to it. */
  inputTokens: number;
  cachedInputTokens: number;
  cacheWriteTokens: number;
  outputTokens: number;
}

/** What `usage` costs on `model`, in USD. */
export function costOf(model: AssistantModelId, usage: AssistantUsage): number {
  const { price } = assistantModels[model];
  return (
    (usage.inputTokens * price.input +
      usage.cachedInputTokens * price.cachedInput +
      usage.cacheWriteTokens * price.cacheWrite +
      usage.outputTokens * price.output) /
    1_000_000
  );
}
